import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../provider/AuthProvider";
import { useUser } from "../../provider/UserProvider";
import { EmployerProfile } from "../../models/User";
import { LoadingSpinner } from "../../components/shared/ui/LoadingSpinner";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { acaPal, acaFont, AcaCTA, AcaIcon } from "../../components/shared/aca";
import { FolioSelect } from "../../components/shared/folio/Select";

const CONTACT_TITLE_OPTIONS = ["Dr.", "Prof.", "Mr.", "Mrs.", "Ms.", "Mx."];

const labelStyle: React.CSSProperties = {
  fontFamily: acaFont.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: acaPal.inkSoft,
  marginBottom: 6,
  display: "block",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "transparent",
  border: "none",
  borderBottom: `1.5px solid ${acaPal.ink}`,
  padding: "10px 0",
  fontFamily: acaFont.sans,
  fontSize: 16,
  color: acaPal.ink,
  outline: "none",
};

const sectionEbrow: React.CSSProperties = {
  fontFamily: acaFont.mono,
  fontSize: 10.5,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: acaPal.inkSoft,
  marginBottom: 8,
};

const h3Style: React.CSSProperties = {
  fontFamily: acaFont.sans,
  fontSize: 28,
  fontWeight: 600,
  letterSpacing: "-0.015em",
  margin: 0,
  color: acaPal.ink,
};

const INSTITUTION_TYPES: { value: "university" | "college" | "research-institute" | "other"; label: string }[] = [
  { value: "university", label: "University" },
  { value: "college", label: "College" },
  { value: "research-institute", label: "Research institute" },
  { value: "other", label: "Other" },
];

const EmployerOnboardingPage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { completeEmployerOnboarding, error: onboardingError } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<EmployerProfile>>({
    address: "",
    uniName: "",
    contactPerson: "",
    phoneNumber: "",
    institutionDescription: "",
    institutionCulture: [],
    institutionType: undefined,
    institutionWebsite: "",
  });
  const [contactTitle, setContactTitle] = useState("");
  const [contactFirstName, setContactFirstName] = useState(user?.firstName || "");
  const [contactLastName, setContactLastName] = useState(user?.lastName || "");
  const [cultureInput, setCultureInput] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddCultureTag = () => {
    if (cultureInput.trim() && formData.institutionCulture && formData.institutionCulture.length < 6) {
      setFormData((prev) => ({
        ...prev,
        institutionCulture: [...(prev.institutionCulture || []), cultureInput.trim()],
      }));
      setCultureInput("");
    }
  };

  const handleRemoveCultureTag = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      institutionCulture: (prev.institutionCulture || []).filter((_, i) => i !== index),
    }));
  };

  const handleInstitutionTypeChange = (value: "university" | "college" | "research-institute" | "other") => {
    setFormData((prev) => ({
      ...prev,
      institutionType: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const contactPerson = [contactTitle, contactFirstName, contactLastName].filter(Boolean).join(" ");
      const rawWebsite = (formData.institutionWebsite || "").trim();
      const institutionWebsite = rawWebsite && !/^https?:\/\//i.test(rawWebsite)
        ? `https://${rawWebsite}`
        : rawWebsite;
      const submitData = { ...formData, contactPerson, institutionWebsite };
      await completeEmployerOnboarding(submitData as any);
      updateUser({
        onboardingCompleted: true,
        profileCompleted: true,
      });
      navigate(ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD);
    } catch (error) {
      console.error("Failed to complete profile:", error);
      alert(onboardingError || "Failed to complete profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== "employer") {
    return (
      <div style={{ background: acaPal.paper, minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", fontFamily: acaFont.sans }}>
          <div style={{ ...sectionEbrow, color: acaPal.accent }}>§ Access denied</div>
          <h1 style={{ ...h3Style, fontSize: 40 }}>This page is for institutions only.</h1>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: acaPal.paper, color: acaPal.ink, fontFamily: acaFont.sans }}>
      {/* Hero */}
      <section style={{ borderBottom: `1px solid ${acaPal.rule}` }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 20px 40px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: 16,
              borderBottom: `1px solid ${acaPal.rule}`,
              paddingBottom: 14,
              fontFamily: acaFont.mono,
              fontSize: 10.5,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: acaPal.inkSoft,
              marginBottom: 32,
            }}
          >
            <div style={{ gridColumn: "span 4" }}>§ Onboarding · Institution</div>
            <div style={{ gridColumn: "span 4", textAlign: "center" }}>Step 03 / 03</div>
            <div style={{ gridColumn: "span 4", textAlign: "right" }}>Reviewed by an editor</div>
          </div>
          <div
            style={{
              fontFamily: acaFont.mono,
              fontSize: 11,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: acaPal.accent,
              marginBottom: 22,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ width: 28, height: 1, background: acaPal.accent }} />§ Complete your institution profile
          </div>
          <h1
            style={{
              fontFamily: acaFont.sans,
              fontSize: "clamp(48px, 7.5vw, 88px)",
              lineHeight: 0.94,
              letterSpacing: "-0.035em",
              fontWeight: 600,
              margin: 0,
              color: acaPal.ink,
              maxWidth: 1100,
            }}
          >
            Introduce your{" "}
            <span style={{ color: acaPal.accent, fontStyle: "italic", fontFamily: acaFont.serif, fontWeight: 400 }}>
              institution
            </span>{" "}
            to the register.
          </h1>
          <p
            style={{
              fontFamily: acaFont.serif,
              fontSize: 20,
              lineHeight: 1.5,
              color: acaPal.inkSoft,
              maxWidth: 760,
              margin: "28px 0 0",
            }}
          >
            Tell us about your university or college. We use this on every listing you publish.
          </p>
        </div>
      </section>

      {/* Form */}
      <section>
        <form onSubmit={handleSubmit} style={{ maxWidth: 880, margin: "0 auto", padding: "56px 20px 96px" }}>
          {/* Institution Info */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ borderBottom: `1px solid ${acaPal.rule}`, paddingBottom: 14, marginBottom: 28 }}>
              <div style={sectionEbrow}>§ 01 · Institution information</div>
              <h3 style={h3Style}>The basics.</h3>
            </div>
            <div style={{ display: "grid", gap: 32 }}>
              <div>
                <label htmlFor="uniName" style={labelStyle}>Institution name *</label>
                <input id="uniName" name="uniName" type="text" value={formData.uniName || ""} onChange={handleChange} placeholder="e.g., University of Cambridge" style={inputStyle} required />
              </div>
              <div>
                <label htmlFor="address" style={labelStyle}>Institution address *</label>
                <textarea id="address" name="address" rows={3} value={formData.address} onChange={handleChange} placeholder="Full address" style={{ ...inputStyle, resize: "vertical" }} required />
              </div>
              <div>
                <label style={labelStyle}>Contact person *</label>
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 16 }}>
                  <FolioSelect
                    ariaLabel="Contact title"
                    placeholder="Title"
                    value={contactTitle}
                    onChange={setContactTitle}
                    options={CONTACT_TITLE_OPTIONS}
                  />
                  <input type="text" value={contactFirstName} onChange={(e) => setContactFirstName(e.target.value)} placeholder="First name" style={inputStyle} required />
                  <input type="text" value={contactLastName} onChange={(e) => setContactLastName(e.target.value)} placeholder="Last name" style={inputStyle} />
                </div>
              </div>
              <div>
                <label htmlFor="phoneNumber" style={labelStyle}>Contact phone number *</label>
                <input id="phoneNumber" name="phoneNumber" type="tel" value={formData.phoneNumber || ""} onChange={handleChange} placeholder="+44 1234 567890" style={inputStyle} required />
              </div>
            </div>
          </div>

          {/* Institution Details */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ borderBottom: `1px solid ${acaPal.rule}`, paddingBottom: 14, marginBottom: 28 }}>
              <div style={sectionEbrow}>§ 02 · Institution details</div>
              <h3 style={h3Style}>What kind of place.</h3>
            </div>
            <div style={{ display: "grid", gap: 32 }}>
              <div>
                <label style={labelStyle}>Institution type</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {INSTITUTION_TYPES.map((opt) => {
                    const checked = formData.institutionType === opt.value;
                    return (
                      <label
                        key={opt.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "14px 16px",
                          border: `1px solid ${checked ? acaPal.ink : acaPal.rule}`,
                          background: checked ? acaPal.card : "transparent",
                          cursor: "pointer",
                          fontFamily: acaFont.sans,
                          fontSize: 14,
                          color: acaPal.ink,
                          transition: "all 120ms ease",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 18,
                            height: 18,
                            border: `1.5px solid ${acaPal.ink}`,
                            background: checked ? acaPal.ink : "transparent",
                            borderRadius: "50%",
                            flexShrink: 0,
                          }}
                        >
                          {checked && <span style={{ width: 6, height: 6, borderRadius: "50%", background: acaPal.paper }} />}
                        </span>
                        <input
                          type="radio"
                          name="institutionType"
                          value={opt.value}
                          checked={checked}
                          onChange={() => handleInstitutionTypeChange(opt.value)}
                          style={{ display: "none" }}
                        />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label htmlFor="institutionWebsite" style={labelStyle}>Institution website</label>
                <input id="institutionWebsite" name="institutionWebsite" type="text" value={formData.institutionWebsite || ""} onChange={handleChange} placeholder="www.yourinstitution.ac.uk" style={inputStyle} />
              </div>
            </div>
          </div>

          {/* About */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ borderBottom: `1px solid ${acaPal.rule}`, paddingBottom: 14, marginBottom: 28 }}>
              <div style={sectionEbrow}>§ 03 · About your institution</div>
              <h3 style={h3Style}>The story.</h3>
            </div>
            <div style={{ display: "grid", gap: 32 }}>
              <div>
                <label htmlFor="institutionDescription" style={labelStyle}>Institution overview *</label>
                <textarea
                  id="institutionDescription"
                  name="institutionDescription"
                  rows={5}
                  value={formData.institutionDescription || ""}
                  onChange={handleChange}
                  placeholder="History, mission, values, and what makes your institution a great place to work…"
                  style={{ ...inputStyle, resize: "vertical" }}
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>Institution culture</label>
                <p style={{ fontFamily: acaFont.mono, fontSize: 10.5, letterSpacing: "0.12em", color: acaPal.inkMute, margin: "0 0 12px", textTransform: "uppercase" }}>
                  Up to 6 tags
                </p>
                {formData.institutionCulture && formData.institutionCulture.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                    {formData.institutionCulture.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 10px",
                          border: `1px solid ${acaPal.ink}`,
                          fontFamily: acaFont.mono,
                          fontSize: 10.5,
                          letterSpacing: "0.18em",
                          textTransform: "uppercase",
                          color: acaPal.ink,
                          background: acaPal.card,
                        }}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveCultureTag(index)}
                          aria-label={`Remove ${tag}`}
                          style={{ background: "transparent", border: "none", padding: 0, color: acaPal.ink, cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                        >
                          <AcaIcon name="close" size={11} stroke={acaPal.ink} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {(!formData.institutionCulture || formData.institutionCulture.length < 6) && (
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        value={cultureInput}
                        onChange={(e) => setCultureInput(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCultureTag())}
                        placeholder="e.g., Innovation, Collaboration, Excellence"
                        style={inputStyle}
                      />
                    </div>
                    <AcaCTA kind="outline" size="sm" type="button" onClick={handleAddCultureTag}>
                      Add
                    </AcaCTA>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 16, justifyContent: "flex-end", paddingTop: 24, borderTop: `1px solid ${acaPal.rule}`, flexWrap: "wrap" }}>
            <AcaCTA kind="outline" size="md" onClick={() => navigate(ROUTE_NAMES.PAGES.HOME)} type="button">
              Skip for now
            </AcaCTA>
            <AcaCTA kind="accent" size="md" type="submit" disabled={loading}>
              {loading ? <LoadingSpinner size="sm" color="white" /> : "Complete profile"}
            </AcaCTA>
          </div>
        </form>
      </section>
    </div>
  );
};

export default EmployerOnboardingPage;
